'use client'

// https://21st.dev/danielpetho/underline-animation/default

import { m, type Transition } from 'motion/react'
import { cn } from '@/lib/utils'

interface UnderlineBaseProps {
  label: string
  className?: string
  onClick?: () => void
  /** When set, renders a real anchor so the link is crawlable/accessible. */
  href?: string
  target?: string
  rel?: string
  underlineHeightRatio?: number
  underlinePaddingRatio?: number
  transition?: Transition
}

export function CenterUnderline({
  label,
  className,
  onClick,
  href,
  target,
  rel,
  transition = { duration: 0.25, ease: 'easeInOut' },
  underlineHeightRatio = 0.1,
  underlinePaddingRatio = 0.01,
  ...props
}: UnderlineBaseProps) {
  // Render a real anchor when href is provided so crawlers and assistive tech
  // see an actual link (a bare onClick span is invisible to both).
  const Tag = href ? m.a : m.span

  return (
    <Tag
      className={cn('relative inline-block cursor-pointer', className)}
      whileHover="visible"
      onClick={onClick}
      href={href}
      target={target}
      rel={rel}
      {...props}
    >
      <span>{label}</span>
      <m.span
        className="absolute left-1/2 -translate-x-1/2 bg-current"
        // `em` resolves against the link's own font size, so the underline
        // scales with the text without measuring it.
        style={{
          height: `${underlineHeightRatio}em`,
          bottom: `${-underlinePaddingRatio}em`,
        }}
        variants={{
          hidden: {
            width: 0,
            originX: 0.5,
          },
          visible: {
            width: '100%',
            transition: transition,
          },
        }}
      />
    </Tag>
  )
}
